import { Dictionary } from '@reduxjs/toolkit';
import { useLiveQuery } from 'dexie-react-hooks';
import { differenceBy, intersectionBy, unionBy } from 'lodash';
import keyBy from 'lodash/keyBy';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useQueryClient, UseQueryOptions } from 'react-query';
import {
  ProjectId,
  Team,
  User,
  UserStatus,
  Order,
  OrderType,
  UserType,
} from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { Project } from 'api/project';
import { ProjectInUserProfile } from 'api/user';
import { Views } from 'components/molecules/views';
import { FormResponse } from 'components/organisms/forms/utils';
import { ColumnOrder } from 'components/organisms/grid-custom-column';
import { TemplateId, TemplateObjectType } from 'data/templates/api';
import { DocumentTemplate } from 'data/templates/documents';
import { ProjectTemplateApi } from 'data/templates/projects';
import { useUser } from 'hooks/use-user';
import { useProjectsQuery } from 'queries';
import { AppUpgradeConfirmationKey } from 'queries/app-upgrade-confirmation';
import { ApiErrorData } from 'queries/error';
import { activeUsersAndTeamsFromIndexedB } from 'queries/members';
import { useProjectDetailQuery } from 'queries/project';
import { logger } from 'services';
import { fullName } from 'utils/full-name';
import { unreachable } from 'utils/unreachable';
import { db } from 'workers';
import { FormResponseDraft } from 'workers/models/form-responses';
import { TemplateDraft } from 'workers/models/project-templates';
import { useProjectAsRBACResource, useWithAccess } from './rbac/useWithAccess';

export const useInitIndexDbListeners = () => {
  const queryClient = useQueryClient();

  db.on('ready', (e) => {
    logger.log('db ready with version', e?.verno);
  });

  db.on('close', (e) => {
    logger.log('db close event fired', e);
  });

  db.on('versionchange', (e) => {
    /**
     * versionchange event is fired in any open windows when a new window is opened which connects to a different version of indexD
     */
    logger.log('db versionchange', e);

    queryClient.setQueryData<boolean>(AppUpgradeConfirmationKey, true);
  });
};

const filterNonCustomerUsers = (user) => {
  return user.userType !== UserType.External;
};

export function usePrerenderMembersAndTeamsFromIndexedDb(): void {
  const members = useLiveQuery(() => {
    return db.members.toArray();
  });

  const teams = useLiveQuery(() => {
    return db.teams.toArray();
  });

  activeUsersAndTeamsFromIndexedB.setActiveUsersAndTeams = {
    users: (members as User[]) || [],
    teams: (teams as Team[]) || [],
  };
}

export function useGetAllNativeUsersFromIndexedDb(): User[] {
  const data = useLiveQuery(() => {
    return db.members
      .where({
        userType: UserType.Native,
      })
      .toArray();
  });

  return data || [];
}

export function useGetAllActiveNativeUsersFromIndexedDb(): User[] {
  const data = useLiveQuery(() => {
    return db.members
      .where({
        userType: UserType.Native,
      })
      .toArray();
  });

  return data || [];
}

export function useGetAllActivePartnerUsersFromIndexedDb(): User[] {
  const data = useLiveQuery(() => {
    return db.members
      .where({
        userType: UserType.Partner,
      })
      .toArray();
  });

  return data || [];
}

export function useGetActiveOrInvitedNonCustomerUsersFromIndexedDb(): User[] {
  const data = useLiveQuery(() => {
    return db.members.filter(filterNonCustomerUsers).toArray();
  });

  return data || [];
}

export function useGetAllMembersBasedonFilter(
  filter: Record<string, unknown>,
): User[] {
  const data = useLiveQuery(() => {
    return db.members.where(filter).toArray();
  });

  return data || [];
}

export function useGetUserByUserId(userId?: number): User | undefined {
  const data = useLiveQuery(() => {
    if (!userId) {
      return undefined;
    }

    return db.members.where('userId').equals(userId).first();
  }, [userId]);

  return data || undefined;
}

export function useGetTeamsByProjectId(projectIds: number[]): {
  data: Record<string, Team[]>;
  isLoading: boolean;
} {
  const data = useLiveQuery(async () => {
    return db.teams
      .toArray()
      .then((teams) => {
        const teamsByProjectIds: Record<string, Team[]> = {};

        projectIds.forEach((projectId) => {
          const teamList = teams.filter((t) =>
            t.projectIds?.includes?.(projectId),
          );

          teamsByProjectIds[projectId] = teamList;
        });

        return teamsByProjectIds;
      })
      .catch((err) => {
        logger.error('Error occured while fetching data from indexed db', err);
      });
  }, [projectIds]);

  return useMemo(
    () =>
      data != null ? { data, isLoading: false } : { data: {}, isLoading: true },
    [data],
  );
}

export function useGetAllMembersInProjects({
  projectIds,
  isPrivate,
}: {
  projectIds: number[];
  isPrivate: boolean;
}): User[] {
  const conditionalFilter = (user: User) => {
    let filter = true;

    if (isPrivate) {
      filter = filter && user.userType === UserType.Native;
    }

    return filter;
  };

  const data = useLiveQuery(async () => {
    return db.members
      .where('projectIds')
      .anyOf(projectIds)
      .filter(conditionalFilter)
      .distinct()
      .toArray();
  }, [projectIds]);

  return data || [];
}

export function useGetAllMembersNotInProjects({
  projectIds,
  isPrivate,
}: {
  projectIds: number[];
  isPrivate: boolean;
}): User[] {
  const conditionalFilter = (user: User) => {
    let filter = true;

    if (isPrivate) {
      filter = filter && user.userType === UserType.Native;
    }

    return filter;
  };

  const data = useLiveQuery(() => {
    return db.members
      .where('projectIds')
      .anyOf(projectIds)
      .filter(conditionalFilter)
      .toArray();
  }, [projectIds]);

  return data || [];
}
// having this to avoid circular dependency
export const useProjectDetail = (
  projectId: ProjectId,
  options: UseQueryOptions<Project, ApiErrorData, Project, ProjectId> = {},
) => {
  return useProjectDetailQuery(projectId, {
    ...options,
    retry: 1,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    enabled: typeof projectId?.[1] === 'number' && projectId?.[1] !== -1,
  });
};

interface ProjectMembersOptions {
  includeRestricedPartnersMembers?: boolean;
}

export function useGetAllMembersInProject({
  isPrivate = false,
  projectId,
  options = {
    includeRestricedPartnersMembers: true,
  },
}: {
  projectId?: number;
  isPrivate?: boolean;
  options?: ProjectMembersOptions;
}): User[] {
  const { data: project } = useProjectDetail(ProjectId(projectId ?? -1));
  const permissivePartnerCompanyIdsInProject = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          (partner) =>
            partner.accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Permissive,
        )
        .map((partner) => partner.companyId),
    [project?.partners],
  );
  const restricedPartnerCompanyIdsInProject = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          (partner) =>
            partner.accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Restricted,
        )
        .map((partner) => partner.companyId),
    [project?.partners],
  );
  const filterForPrivateResource = useCallback(
    (user: User) =>
      user.userType === UserType.Native ||
      (user.userType === UserType.Partner &&
        permissivePartnerCompanyIdsInProject.includes(user.company.companyId)),
    [permissivePartnerCompanyIdsInProject],
  );
  const filterOutRestrictedPartners = useCallback(
    (user: User) => {
      return (
        user.userType !== UserType.Partner ||
        !restricedPartnerCompanyIdsInProject.includes(user.company.companyId)
      );
    },
    [restricedPartnerCompanyIdsInProject],
  );
  const conditionalFilter = useCallback(
    (user: User) =>
      (options.includeRestricedPartnersMembers === true ||
        filterOutRestrictedPartners(user)) &&
      (isPrivate ? filterForPrivateResource(user) : true),
    [
      filterForPrivateResource,
      filterOutRestrictedPartners,
      isPrivate,
      options.includeRestricedPartnersMembers,
    ],
  );

  const data = useLiveQuery(() => {
    if (!projectId) {
      return [];
    }

    return db.members
      .where('projectIds')
      .equals(projectId)
      .filter(conditionalFilter)
      .toArray();
  }, [projectId, conditionalFilter]);

  return data || [];
}

export function useGetAllUsers({
  includeDeactivatedUsers = false,
}: {
  includeDeactivatedUsers?: boolean;
}): User[] {
  const data = useLiveQuery(() => {
    return db.members
      .filter((user) => {
        return includeDeactivatedUsers
          ? true
          : user.userStatus !== UserStatus.Inactive;
      })
      .toArray();
  });

  return data || [];
}

export function useGetAllActiveUsersMap(): Record<number, User> {
  const users = useLiveQuery(() => {
    return db.members
      .where('userStatus')
      .notEqual(UserStatus.Inactive)
      .toArray();
  });

  return keyBy(users, 'userId') || {};
}

export function useGetAllNativeUsersMap(): Record<number, User> {
  const users = useLiveQuery(() => {
    return db.members
      .filter((user) => user.userType === UserType.Native)
      .toArray();
  });

  return keyBy(users, 'userId') || {};
}

export function useGetAllNativeAndPartnerUsersMap(): Record<number, User> {
  const users = useLiveQuery(() => {
    return db.members
      .filter((user) => user.userType !== UserType.External)
      .toArray();
  });

  return keyBy(users, 'userId') || {};
}

export function useGetAllNativeActiveUsersMap(): Record<number, User> {
  const users = useLiveQuery(() => {
    return db.members
      .where('userStatus')
      .notEqual(UserStatus.Inactive)
      .filter((user) => user.userType === UserType.Native)
      .toArray();
  });

  return keyBy(users, 'userId') || {};
}

const useFilterForUserInviteableInProject = () => {
  const { user: currentUser } = useUser();

  const getFilterUserInviteableInProject = useCallback(
    (
      project: Project,
      isPrivate: boolean,
      options: ProjectMembersOptions = {
        includeRestricedPartnersMembers: true,
      },
    ) => {
      const customerCompanyId = project.customer.companyId;
      const currentUserCompanyFromProjectPartners =
        currentUser.userType === UserType.Partner
          ? (project.partners ?? []).find(
              (partner) => partner.companyId === currentUser.company.companyId,
            )
          : undefined;
      const partnersInProject = (project.partners ?? []).map(
        ({ companyId }) => companyId,
      );
      const restricedPartnerCompanyIdsInProject = (project.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Restricted,
        )
        .map(({ companyId }) => companyId);
      const partnersWhoCanAccessPrivateResources = (project?.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Permissive,
        )
        .map(({ companyId }) => companyId);

      const canBeInvitedByVendorOnPrivateResource = (user: User) =>
        user.userType === UserType.Native ||
        (user.userType === UserType.Partner &&
          partnersWhoCanAccessPrivateResources.includes(
            user.company.companyId,
          ));
      const canBeInvitedByVendorOnSharedesource = (user: User) =>
        user.userType === UserType.Native ||
        (user.userType === UserType.Partner &&
          partnersInProject.includes(user.company.companyId) &&
          (options.includeRestricedPartnersMembers === true ||
            !restricedPartnerCompanyIdsInProject.includes(
              user.company.companyId,
            ))) ||
        (user.userType === UserType.External &&
          user.company.companyId === customerCompanyId);

      const canBeInvitedByVendor = (user: User) =>
        isPrivate
          ? canBeInvitedByVendorOnPrivateResource(user)
          : canBeInvitedByVendorOnSharedesource(user);
      const canBeInvitedByCustomer = (user: User) =>
        isPrivate ? false : user.company.companyId === customerCompanyId;

      const canBeInvitedByPartner = (user: User) => {
        if (currentUserCompanyFromProjectPartners == null) {
          return false;
        }
        if (
          isPrivate &&
          currentUserCompanyFromProjectPartners.accessLevel
            ?.partnerPermissionSetName !== PartnerPermissionName.Permissive
        ) {
          return false;
        }

        return user.company.companyId === currentUser.company.companyId;
      };
      const canBeInvitedByCurrentUser = (user: User) => {
        switch (currentUser.userType) {
          case UserType.Native:
            return canBeInvitedByVendor(user);
          case UserType.External:
            return canBeInvitedByCustomer(user);
          case UserType.Partner:
            return canBeInvitedByPartner(user);
          default:
            return unreachable(currentUser.userType);
        }
      };

      return canBeInvitedByCurrentUser;
    },
    [currentUser.company.companyId, currentUser.userType],
  );

  return {
    getFilterUserInviteableInProject,
  };
};

// if UserType === NATIVE then load all native users who are not in this project
// if UserType === EXTERNAL then load all cusatomer users who are not in this project
export function useGetAllMembersNotInProject({
  projectId,
  isPrivate = false,
  options = {
    includeRestricedPartnersMembers: true,
  },
}: {
  projectId?: number;
  isPrivate?: boolean;
  options?: ProjectMembersOptions;
}): User[] {
  const { data: project } = useProjectDetail(ProjectId(projectId ?? -1));
  const projectResource = useProjectAsRBACResource(projectId);
  const customerCompanyId = project?.customer.companyId;
  const { user: currentUser } = useUser();
  const currentUserCompanyFromProjectPartners = useMemo(
    () =>
      currentUser.userType === UserType.Partner
        ? (project?.partners ?? []).find(
            (partner) => partner.companyId === currentUser.company.companyId,
          )
        : undefined,
    [currentUser.company.companyId, currentUser.userType, project?.partners],
  );
  const partnersInProject = useMemo(
    () => (project?.partners ?? []).map(({ companyId }) => companyId),
    [project?.partners],
  );

  const restricedPartnerCompanyIdsInProject = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Restricted,
        )
        .map(({ companyId }) => companyId),
    [project?.partners],
  );

  const partnersWhoCanAccessPrivateResources = useMemo(
    () =>
      (project?.partners ?? [])
        .filter(
          ({ accessLevel }) =>
            accessLevel?.partnerPermissionSetName ===
            PartnerPermissionName.Permissive,
        )
        .map(({ companyId }) => companyId),
    [project?.partners],
  );

  const canBeInvitedByVendorOnPrivateResource = useCallback(
    (user: User) =>
      user.userType === UserType.Native ||
      (user.userType === UserType.Partner &&
        partnersWhoCanAccessPrivateResources.includes(user.company.companyId)),
    [partnersWhoCanAccessPrivateResources],
  );
  const canBeInvitedByVendorOnSharedesource = useCallback(
    (user: User) =>
      user.userType === UserType.Native ||
      (user.userType === UserType.Partner &&
        partnersInProject.includes(user.company.companyId) &&
        (options.includeRestricedPartnersMembers === true ||
          !restricedPartnerCompanyIdsInProject.includes(
            user.company.companyId,
          ))) ||
      (user.userType === UserType.External &&
        user.company.companyId === customerCompanyId),
    [
      customerCompanyId,
      options.includeRestricedPartnersMembers,
      partnersInProject,
      restricedPartnerCompanyIdsInProject,
    ],
  );

  const canBeInvitedByVendor = useCallback(
    (user: User) =>
      isPrivate
        ? canBeInvitedByVendorOnPrivateResource(user)
        : canBeInvitedByVendorOnSharedesource(user),

    [
      canBeInvitedByVendorOnPrivateResource,
      canBeInvitedByVendorOnSharedesource,
      isPrivate,
    ],
  );
  const canBeInvitedByCustomer = useCallback(
    (user: User) =>
      isPrivate ? false : user.company.companyId === customerCompanyId,
    [customerCompanyId, isPrivate],
  );
  const canBeInvitedByPartner = useCallback(
    (user: User) => {
      if (currentUserCompanyFromProjectPartners == null) {
        return false;
      }
      if (
        isPrivate &&
        currentUserCompanyFromProjectPartners.accessLevel
          ?.partnerPermissionSetName !== PartnerPermissionName.Permissive
      ) {
        return false;
      }

      return user.company.companyId === currentUser.company.companyId;
    },
    [
      currentUser.company.companyId,
      currentUserCompanyFromProjectPartners,
      isPrivate,
    ],
  );
  const { hasAccessToResource } = useWithAccess();

  const {
    canInviteCustomerUsers,
    canInviteVendorUsers,
    canInvitePartnerUsers,
  } = useMemo(
    () => ({
      canInviteVendorUsers: hasAccessToResource({
        rule: 'PROJECT_MEMBER:CREATE',
        resourceData: projectResource,
        actionScope: ['NATIVE'],
      }),
      canInviteCustomerUsers: hasAccessToResource({
        rule: 'PROJECT_MEMBER:CREATE',
        resourceData: projectResource,
        actionScope: ['EXTERNAL'],
      }),
      canInvitePartnerUsers: hasAccessToResource({
        rule: 'PROJECT_MEMBER:CREATE',
        resourceData: projectResource,
        actionScope: ['PARTNER'],
      }),
    }),
    [hasAccessToResource, projectResource],
  );
  const hasAccessToInviteThisUser = useCallback(
    (user: User) => {
      switch (user.userType) {
        case UserType.Native:
          return canInviteVendorUsers;
        case UserType.External:
          return canInviteCustomerUsers;
        case UserType.Partner:
          return canInvitePartnerUsers;
        default:
          return unreachable(user.userType);
      }
    },
    [canInviteCustomerUsers, canInvitePartnerUsers, canInviteVendorUsers],
  );
  const canBeInvitedByCurrentUser = useCallback(
    (user: User) => {
      switch (currentUser.userType) {
        case UserType.Native:
          return canBeInvitedByVendor(user) && hasAccessToInviteThisUser(user);
        case UserType.External:
          return (
            canBeInvitedByCustomer(user) && hasAccessToInviteThisUser(user)
          );
        case UserType.Partner:
          return canBeInvitedByPartner(user);
        default:
          return unreachable(currentUser.userType);
      }
    },
    [
      currentUser.userType,
      canBeInvitedByVendor,
      hasAccessToInviteThisUser,
      canBeInvitedByCustomer,
      canBeInvitedByPartner,
    ],
  );

  const conditionalFilter = useCallback(
    (user: User) => {
      if (!projectId) {
        return false;
      }

      return (
        !user.projectIds?.includes(projectId) && canBeInvitedByCurrentUser(user)
      );
    },
    [canBeInvitedByCurrentUser, projectId],
  );

  const data = useLiveQuery(() => {
    if (!projectId) {
      return [];
    }

    return db.members.filter(conditionalFilter).toArray();
  }, [conditionalFilter]);

  return data || [];
}

export function useGetAllMembersByCompany(companyId: number): User[] {
  const data = useLiveQuery(async () => {
    if (!companyId) {
      return undefined;
    }

    return db.members.where('company.companyId').equals(companyId).toArray();
  }, [companyId]);

  return data || [];
}

export function useGetAllTeamsFromIndexedDb(): Team[] {
  const data = useLiveQuery(() => {
    return db.teams.toArray();
  });

  return data || [];
}

export function useGetAllTeamsFromIndexedDbConditionally(
  projectId?: number,
): Team[] {
  const data = useLiveQuery(async () => {
    if (projectId) {
      return db.teams
        .filter((team) => !!team?.projectIds?.includes?.(projectId))
        .toArray();
    }

    return db.teams.filter((team) => !!team?.accountLevelTeam).toArray();
  });

  return data || [];
}

export function useGetUserFromIndexedDb(userId?: number): User {
  const data = useLiveQuery(() => {
    return userId
      ? db.members
          .where({
            userId,
          })
          .first()
      : undefined;
  }, [userId]);

  return data || ({} as User);
}

export async function getUserFromIndexedDb(
  userId: number,
): Promise<User | undefined> {
  const user = await db.members
    .where({
      userId,
    })
    .first();

  return user;
}

export function useGetProjectIdsOfCurrentUserFromIndexedDb(): number[] {
  const {
    user: { userId },
  } = useUser();
  const data = useLiveQuery(() => {
    return userId
      ? db.members
          .where({
            userId,
          })
          .first()
      : undefined;
  });

  return data?.projectIds || [];
}

export function useGetUsersFromIndexedDb(userIds: number[]): User[] {
  const data = useLiveQuery(() => {
    return userIds.length
      ? db.members.where('userId').anyOf(userIds).toArray()
      : [];
  }, [userIds]);

  return data || [];
}

export function useGetTeamsFromIndexedDb(teamIds: number[]): Team[] | [] {
  const data = useLiveQuery(() => {
    return teamIds.length
      ? db.teams.where('teamId').anyOf(teamIds).toArray()
      : [];
  }, [teamIds]);

  return data || [];
}

export function useFullName(userId?: number): string {
  const user = useGetUserFromIndexedDb(userId);

  return fullName(user as User);
}

async function getAllColumnOrderFromIndexedDb(
  orderType: OrderType,
): Promise<Order[]> {
  const data = await db.columnOrder.where({ orderType }).toArray();

  return data;
}

export async function getColumnOrderFromIndexedDb(key: string): Promise<Order> {
  const data = await db.columnOrder.where({ key }).toArray();

  return data[0];
}

async function doesColumnOrderExist(key: string): Promise<boolean> {
  try {
    const data = await db.columnOrder.get(key);

    return data !== undefined;
  } catch (err) {
    return false;
  }
}

export async function addUpdateColumnOrderIndexedDb(
  key: string,
  viewId: number,
  columnOrder: ColumnOrder[],
  orderType: OrderType,
): Promise<string> {
  const doesExist = await doesColumnOrderExist(key);

  let payload: Order = {
    key,
    viewId,
    columnOrder,
    orderType,
  };

  if (doesExist) {
    const oldData = await db.columnOrder.get(key);

    payload = { ...payload, createdAt: oldData?.createdAt };
  } else {
    payload = { ...payload, createdAt: new Date().getTime() };
  }
  const data = await db.columnOrder.put(payload, key);

  return data;
}

export async function deleteColumnOrderFromIndexedDb<T>(
  orderType: OrderType,
  currentListViews: Views<T>,
): Promise<void> {
  const idbList = await getAllColumnOrderFromIndexedDb(orderType);

  if (idbList.length === 0 && currentListViews.ids[0] !== 'just') {
    return;
  }
  const deletedViewKeys = idbList
    .filter(
      (x) =>
        currentListViews.ids[0] === 'just' &&
        x.viewId >= 0 &&
        !currentListViews?.ids?.[1]?.includes(x.viewId),
    )
    .map((item) => item.key);

  if (deletedViewKeys.length > 0) {
    await db.columnOrder.bulkDelete(deletedViewKeys);
  }
}

export async function addProjectsToIndexDb(
  projects: ProjectInUserProfile[],
): Promise<void> {
  await db.projects.clear();
  await db.projects.bulkAdd(projects);
}

export const useProjectsLightFromIndexDb = (
  search: string,
): ProjectInUserProfile[] => {
  return (
    useLiveQuery(() =>
      db.projects
        .filter((project) => project.projectName.includes(search))
        .toArray(),
    ) || []
  );
};
export async function getFormResponseDraftFromIndexedDb(
  formInstance: number,
): Promise<FormResponseDraft> {
  const data = await db.formResponseDraft.where({ formInstance }).toArray();

  return data[0];
}

export async function addUpdateFormResponseDraftIndexedDb(
  formInstance: number,
  response: FormResponse,
): Promise<number> {
  const data = await db.formResponseDraft.put(
    {
      response,
      timestamp: moment().valueOf(),
      formInstance,
    },
    formInstance,
  );

  return data;
}

export async function deleteFormResponseFromIndexedDb(
  formInstance: number,
): Promise<void> {
  return db.formResponseDraft.delete(formInstance);
}
interface ProjectUsersType {
  inProjectUsers: User[];
  notInProjectUsers: User[];
}

export const useMembersInAndNotInProjects = ({
  projectIds,
  isPrivate,
}: {
  projectIds: number[];
  isPrivate: boolean;
}): ProjectUsersType => {
  const activeUsers = useLiveQuery(() => db.members.toArray());
  const { getFilterUserInviteableInProject } =
    useFilterForUserInviteableInProject();
  const { data: allProjects } = useProjectsQuery();

  return useMemo(() => {
    const projectLookup = keyBy(allProjects, 'projectId');
    const projects = projectIds.map((id) => projectLookup[id]).filter(Boolean);

    const usersBelongingToProjects: Dictionary<ProjectUsersType> =
      projects.reduce((previous, project) => {
        const permissivePartnerCompanyIdsInProject = (project?.partners ?? [])
          .filter(
            (partner) =>
              partner.accessLevel?.partnerPermissionSetName ===
              PartnerPermissionName.Permissive,
          )
          .map((partner) => partner.companyId);
        const inProjectUser = (user: User) =>
          user.projectIds?.includes(project.projectId);
        const permissivePartnerUser = (user: User) =>
          permissivePartnerCompanyIdsInProject.includes(user.company.companyId);
        const assignableInPrivateResource = (user: User) =>
          user.userType === UserType.Native || permissivePartnerUser(user);
        const filterFunctionForInviteableUser =
          getFilterUserInviteableInProject(project, isPrivate);
        const inProjectUsers = (activeUsers ?? []).filter(
          (user) =>
            inProjectUser(user) &&
            (isPrivate ? assignableInPrivateResource(user) : true),
        );
        const notInProjectUsers = (activeUsers ?? []).filter(
          (user) =>
            !inProjectUser(user) && filterFunctionForInviteableUser(user),
        );

        return {
          ...previous,
          [project.projectId]: {
            inProjectUsers,
            notInProjectUsers,
          },
        };
      }, {});

    const usersInProjects = projectIds.reduce((previous, projectId) => {
      const match = usersBelongingToProjects[projectId];

      if (match == null) {
        return previous;
      }

      return intersectionBy(
        previous,
        unionBy(match.inProjectUsers, match.notInProjectUsers, 'userId'),
        'userId',
      );
    }, activeUsers ?? []);

    const inProjectUsers = projectIds.reduce((previous, projectId) => {
      return intersectionBy(
        previous,
        usersBelongingToProjects[projectId]?.inProjectUsers ?? [],
        'userId',
      );
    }, usersInProjects);

    const notInProjectUsers = differenceBy(
      usersInProjects,
      inProjectUsers,
      'userId',
    );

    return {
      inProjectUsers,
      notInProjectUsers,
    };
  }, [
    activeUsers,
    allProjects,
    getFilterUserInviteableInProject,
    isPrivate,
    projectIds,
  ]);
};

export async function getTemplateDraftFromIndexedDb(
  templateId: TemplateId,
): Promise<TemplateDraft> {
  const data = await db.templateDraft.where({ templateId }).toArray();

  return data[0];
}

export async function addUpdateTemplateDraftIndexedDb(
  templateId: TemplateId,
  content: ProjectTemplateApi | DocumentTemplate,
  templateType: TemplateObjectType,
): Promise<TemplateId> {
  const data = await db.templateDraft.put(
    {
      content,
      timestamp: moment().valueOf(),
      templateId,
      templateType,
    },
    templateId,
  );

  return data;
}

export async function deleteTemplateFromIndexedDb(
  templateIds: TemplateId[],
): Promise<void> {
  return db.templateDraft.bulkDelete(templateIds);
}

export async function clearColumnOrderTableFromIndexedDb() {
  await db.columnOrder.clear();
}
